import React, { useState } from 'react';
import { ShieldAlert, ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * Driver Profile Photo Policy notice — surfaced right next to the
 * profile photo upload control so drivers see it BEFORE picking an image.
 *
 * Collapsible to keep the dashboard tidy after first read, but the headline
 * + key prohibition stays visible at all times.
 */
export default function ProfilePhotoPolicyNotice({ className = '' }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      className={`bg-amber-50 border border-amber-200 rounded-md p-3 sm:p-4 ${className}`}
      data-testid="profile-photo-policy-notice"
    >
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 bg-amber-100 rounded-md flex items-center justify-center flex-shrink-0">
          <ShieldAlert className="w-4 h-4 text-amber-700" strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p
              className="text-sm font-semibold text-amber-900"
              data-testid="profile-photo-policy-headline"
            >
              Profile photo policy — read before uploading
            </p>
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="text-amber-700 hover:text-amber-900 flex-shrink-0"
              aria-label={expanded ? 'Collapse policy details' : 'Expand policy details'}
              data-testid="profile-photo-policy-toggle"
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>
          <p className="text-xs text-amber-800 mt-1 leading-relaxed">
            <strong>No</strong> business logos, branding, phone numbers, emails, URLs,
            QR codes or social handles. Your photo should identify <em>you</em>, not
            advertise services.
          </p>
          {expanded && (
            <div
              className="mt-2 space-y-1.5 text-xs text-amber-800 leading-relaxed"
              data-testid="profile-photo-policy-details"
            >
              <p>
                Drivers are not permitted to upload profile photos containing
                business logos, company branding, phone numbers, email addresses,
                website URLs, QR codes, social media handles, or any other contact
                details.
              </p>
              <p>
                Profile photos must be used solely to identify the driver and must
                not be used for advertising or promoting services outside the
                JustDeliver platform.
              </p>
              <p>
                Any driver found to be using a profile photo that breaches this
                policy may have the image removed and may receive a warning,
                temporary suspension, or permanent suspension of their account, at
                JustDeliver&apos;s discretion.
              </p>
              <p className="pt-1">
                <Link
                  to="/terms#terms-profile-photo-policy"
                  className="text-amber-900 underline hover:text-amber-700"
                  data-testid="profile-photo-policy-terms-link"
                >
                  Read the full policy in our Terms &amp; Conditions →
                </Link>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
